import React, { Fragment, PureComponent } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';

import classNames from 'classnames';
import { injectStyles } from 'injectStyles';
import * as cssVariable from 'cssVariables';

import checkUserCode from 'util/checkUserCode';

const inputs = [0, 1, 2, 3, 4, 5, 6, 7];
class SyncScreen extends PureComponent {
  static defaultProps = {
    isLoading: false,
    title: 'OP JE TV',
    textAbove: `Koppel je JUKE-profiel!${'\n'}Vul hieronder de code in die op je tv te zien is:`,
    email: 'support@juke.nl',
  };

  static propTypes = {
    title: PropTypes.string,
    textAbove: PropTypes.string,
    email: PropTypes.string,
    isConnected: PropTypes.bool.isRequired,
    isLoading: PropTypes.bool,
    setConnected: PropTypes.func.isRequired,
    setLoading: PropTypes.func.isRequired,
    setError: PropTypes.func.isRequired,
    classes: PropTypes.object.isRequired,
    hasError: PropTypes.bool.isRequired,
  };

  inputRefs = [];

  componentDidMount() {
    this.handleAutofocus(0);
  }

  handleChangeNumber = (elementIndex) => (e) => {
    if (e.target.value === '' || !/^[a-zA-Z]$/.test(e.target.value)) {
      this.inputRefs[elementIndex].value = '';
    } else {
      this.props.setError(false);
      this.inputRefs[elementIndex].value = e.target.value.toUpperCase();
      this.handleNextElement(elementIndex);
    }
  };

  handleNextElement = (elementIndex) => {
    if (elementIndex < 7 && this.inputRefs[elementIndex + 1].value === '') {
      this.handleAutofocus(elementIndex + 1);
    } else {
      // input after the current one // all inputs
      const emptyRef =
        this.inputRefs.slice(elementIndex + 1).find((ref) => ref.value === '') ||
        this.inputRefs.find((ref) => ref.value === '');

      if (emptyRef) {
        emptyRef.focus();
      } else {
        this.inputRefs[elementIndex].blur();
      }
    }

    if (!this.props.isConnected) {
      this.handleConnect();
    }
  };

  handleKeyDown = (index) => (event) => {
    event.persist();
    if (event.key === 'Backspace' && index > 0 && event.target.value === '') {
      this.inputRefs[index - 1].value = '';
      this.inputRefs[index - 1].focus();
    }
  };

  handleConnect = async () => {
    // Add dash to center position
    const userCode = this.inputRefs.reduce((acc, ref, index) => `${acc}${index === 4 ? '-' : ''}${ref.value}`, '');

    if (!userCode || userCode.length !== 9) {
      return;
    }

    const { setLoading, setConnected } = this.props;

    setLoading(true);

    const isValidCode = await checkUserCode(userCode);

    if (isValidCode) {
      setConnected(true);
      setLoading(false);
    } else {
      setConnected(false);
      setLoading(false);
      this.inputRefs.forEach((ref) => {
        // Clear inputs
        ref.value = '';
      });
      this.handleAutofocus(0);
      this.props.setError(true);
    }
  };

  handleFocus = (event) => {
    event.target.select();
  };

  handleAutofocus = (curr) => {
    if (this.inputRefs[curr]) {
      this.inputRefs[curr].focus();
      setTimeout(() => {
        if (this.inputRefs[curr]) this.inputRefs[curr].focus();
      }, 100);
    }
  };

  setRef = (index) => (ref) => {
    this.inputRefs[index] = ref;
  };

  hasValue = (index) => this.inputRefs[index] && this.inputRefs[index].value;

  render() {
    const { title, textAbove, isLoading, email, classes } = this.props;

    return (
      <div className={classes.wrapper}>
        {title && <div className={classes.title}>{title}</div>}
        {textAbove && <div className={classes.text}>{textAbove}</div>}
        <form className={classes.formInput}>
          {inputs.map((index) => (
            <Fragment key={index}>
              <input
                autoComplete="off"
                ref={this.setRef(index)}
                onChange={this.handleChangeNumber(index)}
                onKeyDown={this.handleKeyDown(index)}
                className={classNames(classes.squareInput, {
                  [classes.red]: this.props.hasError && !this.hasValue(index),
                })}
                type="text"
                maxLength={1}
                onFocus={this.handleFocus}
                disabled={isLoading}
              />
              {index === 3 && <span className={classes.dash}>-</span>}
            </Fragment>
          ))}
        </form>
        {
          <div className={classes.text}>
            {'Lukt het niet? Check de '}
            <Link target="_blank" className={classes.pageLink} to="/veelgestelde-vragen">
              veelgestelde vragen
            </Link>
            {'\n'}
            {'of stuur een mailtje naar '}
            {<a href={`mailto:${email}`}>{email}</a>}
          </div>
        }
      </div>
    );
  }
}

const styles = (theme) => ({
  wrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'column',
    alignItems: 'center',
  },
  title: {
    marginBottom: 7,
    fontSize: 27,
    fontWeight: cssVariable.fontWeights.bold,
  },
  text: {
    padding: '15px 0',
    fontSize: cssVariable.fontSizes.medium,
    whiteSpace: 'pre-wrap',
    textAlign: 'center',
  },
  formInput: {
    display: 'flex',
    height: 70,
    marginBottom: 15,
  },
  squareInput: {
    borderStyle: 'solid',
    width: 62.07,
    height: 69.83,
    margin: 7.76,
    borderColor: cssVariable.green500,
    borderRadius: 7.76,
    textAlign: 'center',
    fontSize: cssVariable.fontSizes.xxlarge,
    '&:focus': {
      border: 2,
      borderColor: cssVariable.blue500,
      borderStyle: 'solid',
      borderRadius: 7.76,
    },
  },
  dash: {
    width: 30,
    height: 69.83,
    lineHeight: '69.83px',
    margin: 7.76,
    textAlign: 'center',
    fontSize: cssVariable.fontSizes.xxlarge,
  },
  red: {
    borderColor: cssVariable.red500,
  },
  pageLink: {
    color: cssVariable.green500,
  },
  [theme.breakpoints.down('sm')]: {
    squareInput: {
      width: 38,
      height: 45,
      margin: 5,
      fontSize: cssVariable.fontSizes.large,
    },
    dash: {
      width: 28,
      height: 45,
      lineHeight: '45px',
      margin: 5,
      fontSize: cssVariable.fontSizes.large,
    },
  },
  [theme.breakpoints.down('xs')]: {
    wrapper: {
      margin: '0 -15px',
    },
    squareInput: {
      width: 32,
      height: 36,
      margin: 2,
      fontSize: cssVariable.fontSizes.medium,
    },
    dash: {
      width: 18,
      height: 35,
      lineHeight: '35px',
      margin: 2,
      fontSize: cssVariable.fontSizes.large,
    },
  },
});

export default injectStyles(styles)(SyncScreen);
